/**
 * US geography reference data shared by address and filter UIs.
 *
 * State codes are the USPS two-letter abbreviations the shipment API stores on
 * `StopBrief.state`, so they are also what `pickup_states`/`delivery_states`
 * match on. Names are proper nouns rather than product copy, so they live here
 * as data instead of in the i18n catalogue.
 */
export interface UsState {
  code: string
  name: string
}

export const US_STATES: readonly UsState[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

const US_STATE_NAME_BY_CODE = new Map(US_STATES.map((s) => [s.code, s.name]))

export function getUsStateName(code: string): string | undefined {
  return US_STATE_NAME_BY_CODE.get(code)
}

/**
 * Shipping time zones, keyed by the IANA zone the shipment API stores (the
 * geocode endpoint returns an IANA `timezone` per address), which is what the
 * list endpoint's `timezones` param matches on. Labels are product copy, so
 * they resolve through i18n rather than being baked in here.
 */
export interface UsTimezone {
  value: string
  labelI18nKey: string
}

export const US_TIMEZONES: readonly UsTimezone[] = [
  { value: 'America/New_York', labelI18nKey: 'timezones.eastern' },
  { value: 'America/Chicago', labelI18nKey: 'timezones.central' },
  { value: 'America/Denver', labelI18nKey: 'timezones.mountain' },
  { value: 'America/Los_Angeles', labelI18nKey: 'timezones.pacific' },
  { value: 'America/Anchorage', labelI18nKey: 'timezones.alaska' },
  { value: 'Pacific/Honolulu', labelI18nKey: 'timezones.hawaii' },
]
